import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import Checkbox from 'react-custom-checkbox';
import AuthContext from 'AppContext/AuthProvider';
import { TRole, TSchool } from 'models/auth';
import SchoolPicker from 'components/SchoolPicker';
import Button from 'components/Button';
import { updateUserSchool } from 'models/User/api';
import { logger } from 'utils';
import styles from './studentInfoModal.module.sass';

const rolesToShowModal = [TRole.STUDENT, TRole.TEACHER];

const ROUTES_TO_SHOW_MODAL = [/\/challenges\/\w+/, /\/skillbuilders\/\w+/, /\/dashboard\/+/];

const StudentInfoModal = () => {
  const router = useRouter();
  const { appUser, updateUserSchool: updateUserSchoolInContext } = useContext(AuthContext);
  const [noSuggestions, setNoSuggestions] = useState<boolean>(false);
  const [debouncedNoSuggestions, setDebouncedNoSuggestions] = useState<boolean>(false);
  const [cantFindSchool, setCantFindSchool] = useState<boolean>(false);
  const [selectedSchool, setSelectedSchool] = useState<TSchool | null>(null);
  const [schoolName, setSchoolName] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(true);

  useEffect(() => {
    const pathname = router.asPath?.split('?')[0] ?? '';
    if (ROUTES_TO_SHOW_MODAL.some(route => route.test(pathname))) {
      setIsOpen(true);
    }
  }, [router.pathname, router.asPath]);

  useEffect(() => {
    if (!noSuggestions) {
      setDebouncedNoSuggestions(false);
      return () => {};
    }
    const timer = setTimeout(() => {
      setDebouncedNoSuggestions(noSuggestions);
    }, 1500);
    return () => clearTimeout(timer);
  }, [noSuggestions]);

  if (!appUser || appUser.school_id || appUser.cant_find_school) return null;

  if (!rolesToShowModal.includes(appUser.role)) return null;

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await updateUserSchool(
        appUser.id,
        selectedSchool as TSchool,
        schoolName as string,
        cantFindSchool,
      );
      const newUser = response.data;
      updateUserSchoolInContext(newUser);
      // modal closes automatically after the user is updated in the auth context
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.studentInfoModalContainer}>
      <div className={styles.studentInfoModal}>
        <div className={styles.studentInfoModal__header}>
          <h2>Please type your high school name and select it from the dropdown</h2>
        </div>
        <div className={styles.studentInfoModal__content}>
          <div className={styles.studentState}>
            <p>
              State: <b>{appUser.state}</b> — City: <b>{appUser.city}</b>
            </p>
            <p className={styles.studentState__note}>
              If you need to change your state or city, go to your profile and edit your
              information.
            </p>
          </div>
          <div className={styles.schoolPickerContainer}>
            <SchoolPicker
              city={appUser.city ?? ''}
              onChange={(isSchoolSelected, school) => {
                if (isSchoolSelected) {
                  setSelectedSchool(school as TSchool);
                  setSchoolName((school as TSchool).name);
                  setCantFindSchool(false);
                } else {
                  setSelectedSchool(null);
                  setSchoolName(school as string);
                }
              }}
              stateCode={appUser.state}
              initialSchool={
                {
                  name: appUser.organization ?? '',
                  id: '',
                } as any
              }
              placeholder="School Name"
              fieldName="school"
              onSuggestion={(schools, search) => {
                setNoSuggestions(schools.length === 0 && search.length > 2);
              }}
            />
          </div>
        </div>
        {debouncedNoSuggestions && (
          <div className={styles.cantFindSchoolContainer}>
            <Checkbox
              label="I can't find my school in this list"
              onChange={() => {
                setCantFindSchool(!cantFindSchool);
              }}
              checked={cantFindSchool}
            />
            Write the name of your school so we can use it to add it.
          </div>
        )}
        <div className={styles.studentInfoModal__footer}>
          <div className={styles.studentInfoModal__footer__buttons}>
            <Button
              onClick={handleSubmit}
              theme="Filled - Lime"
              disabled={
                !((cantFindSchool && debouncedNoSuggestions) || selectedSchool) ||
                isLoading ||
                !schoolName
              }
            >
              Confirm
            </Button>
            <Button
              onClick={() => {
                setIsOpen(false);
              }}
              theme="Transparent - Black Border"
            >
              Cancel
            </Button>
          </div>
          <p className={styles.studentInfoModal__footer__note}>
            If you cancel, this message will appear again.
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudentInfoModal;
